import { useState, useCallback, useEffect, useContext, Suspense, useMemo } from "react";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { AuthContext, UIContext } from "../../../../../contexts";
import TabPanel from "../../../../shared/tabPanelComponent/TabPanel";
import a11yProps from "../../../../../constants/tabPanelProps/ayProps";

import { Box, Tabs, Tab } from "@material-ui/core";
import AllEnrollmentsGrid from "./OpenEnrollmentsGrid";
import { OpenEnrollment } from "../../../../../@types/openEnrollment.types";
import { api } from "../../../../../utils/api";
import { LazyOpenEnrollmentsGrid } from "../../..";
import { trackPromise } from "react-promise-tracker";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { manualDateComparator } from "../../../../../utils/commonFunctions/date";
import CircularLoader from "../../../../shared/loader/CircularLoader";
import { getPermissionsFromGroupOwner } from "../../../../../utils/getPermissionsFromGroupOwner";

const OpenEnrollments = (): JSX.Element => {
	const [activeOpenEnrollments, setActiveOpenEnrollments] = useState<OpenEnrollment[]>([]);
	const [upcomingOpenEnrollments, setUpcomingOpenEnrollments] = useState<OpenEnrollment[]>([]);
	const [expiredOpenEnrollments, setExpiredOpenEnrollments] = useState<OpenEnrollment[]>([]);
	const queryGroupNumber = useRouteParam("gn");
	const [loading, setLoading] = useState(true);
	const [tabValue, setTabValue] = useState<number>(0);
	const { setDashboardHeader } = useContext(UIContext);

	const [employeeFilterValue, setEmployeeFilterValue] = useState<any>("");
	const [firstRender, setFirstRender] = useState<any>(false);

	const { user, setUser } = useContext(AuthContext);

	const allCoreEnrollmentPermission = user
		? getPermissionsFromGroupOwner(user, Number(queryGroupNumber))?.plan_control_menu.core_all_enrollments_sub.self
		: null;

	// console.log("allCoreEnrollmentPermission", allCoreEnrollmentPermission);
	// Permission check for tabs
	const showActive = allCoreEnrollmentPermission?.active_tab?.view;
	const showUpcoming = allCoreEnrollmentPermission?.upcoming_tab?.view;
	const showExpired = allCoreEnrollmentPermission?.expired_tab?.view;

	// Build list of visible tabs
	const visibleTabs = useMemo(() => {
		const tabs = [];
		let index = 0;

		if (!showActive && !showUpcoming && !showExpired) return [];

		if (showActive) {
			tabs.push({ label: "Active", index: index++ });
		}
		if (showUpcoming) {
			tabs.push({ label: "Upcoming", index: index++ });
		}
		if (showExpired) {
			tabs.push({ label: "Expired", index: index++ });
		}
		return tabs;
	}, [showActive, showUpcoming, showExpired]);

	const getOpenEnrollments = useCallback(async () => {
		setLoading(true);
		const filter: any = {};
		// console.log("calling api employee ssn", employeeFilterValue);
		if (employeeFilterValue && employeeFilterValue.length > 0) {
			filter["employee_SSN"] = employeeFilterValue;
		}
		try {
			const _openEnrollments: OpenEnrollment[] = await trackPromise(
				api.openEnrollment.getOpenEnrollments(filter, Number(queryGroupNumber))
			);

			const _activeOpenEnrollment: OpenEnrollment[] = [];
			const _upcomingOpenEnrollment: OpenEnrollment[] = [];
			const _expiredOpenEnrollment: OpenEnrollment[] = [];
			const currentDate = new Date() as unknown as string;

			for (let openEnrollmentIdx = 0; openEnrollmentIdx < _openEnrollments.length; openEnrollmentIdx++) {
				const startDate = _openEnrollments[openEnrollmentIdx].start_date as string;

				// Manual Comparator is a Custom Date Comparator
				const startDateComparison: number = manualDateComparator(startDate, currentDate);

				if (_openEnrollments[openEnrollmentIdx].end_date) {
					const endDate = _openEnrollments[openEnrollmentIdx].end_date as string;

					if (startDateComparison === -1 || startDateComparison === 0) {
						const endDateComparison: number = manualDateComparator(endDate, currentDate);
						if (endDateComparison === 1 || endDateComparison === 0) {
							_activeOpenEnrollment.push(_openEnrollments[openEnrollmentIdx]);
						} else {
							_expiredOpenEnrollment.push(_openEnrollments[openEnrollmentIdx]);
						}
					} else {
						_upcomingOpenEnrollment.push(_openEnrollments[openEnrollmentIdx]);
					}
				} else {
					if (startDateComparison === -1 || startDateComparison === 0) {
						_activeOpenEnrollment.push(_openEnrollments[openEnrollmentIdx]);
					} else {
						_upcomingOpenEnrollment.push(_openEnrollments[openEnrollmentIdx]);
					}
				}
			}
			setActiveOpenEnrollments(Object.assign([], _activeOpenEnrollment));
			setUpcomingOpenEnrollments(Object.assign([], _upcomingOpenEnrollment));
			setExpiredOpenEnrollments(Object.assign([], _expiredOpenEnrollment));
		} catch (error) {
			// console.log("Eroor in fetching open enrollments", error);
		} finally {
			setLoading(false);
		}
	}, [queryGroupNumber, employeeFilterValue]);

	// eslint-disable-next-line @typescript-eslint/ban-types
	const handleLabelChange = (event: React.ChangeEvent<{}>, value: number) => {
		setTabValue(value);
	};

	useEffect(() => {
		if (queryGroupNumber !== null) {
			getOpenEnrollments();
			setFirstRender(true);
		}
	}, [getOpenEnrollments, queryGroupNumber]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.all_enrollments);
	}, [setDashboardHeader]);

	useEffect(() => {
		if (firstRender) {
			getOpenEnrollments();
		}
	}, [employeeFilterValue]);

	// If no permission to view any tab, return empty
	if (!showActive && !showUpcoming && !showExpired) {
		return <></>;
	}

	return (
		<div className="all-enrollment-container">
			{loading && <CircularLoader />}
			<Box sx={{ width: "100%" }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<Tabs
						value={tabValue}
						onChange={handleLabelChange}
						aria-label="enrollment tabs"
						TabIndicatorProps={{
							style: { border: "none", backgroundColor: "#558b2f" }
						}}
						className="glb-tab-panel"
					>
						{visibleTabs.map((tab, i) => (
							<Tab key={i} label={tab.label} className="glb-tab-label" {...a11yProps(tab.index)} />
						))}
					</Tabs>
				</Box>

				{/* Render TabPanels based on same visibleTabs array */}
				{visibleTabs.map((tab, i) => {
					let content = null;

					switch (tab.label) {
						case "Active":
							content = (
								<LazyOpenEnrollmentsGrid
									openEnrollments={activeOpenEnrollments}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
									status="ACTIVE"
								/>
							);
							break;
						case "Upcoming":
							content = (
								<LazyOpenEnrollmentsGrid
									openEnrollments={upcomingOpenEnrollments}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
									status="UPCOMING"
								/>
							);
							break;
						case "Expired":
							content = (
								<LazyOpenEnrollmentsGrid
									openEnrollments={expiredOpenEnrollments}
									setEmployeeFilterValue={setEmployeeFilterValue}
									employeeFilterValue={employeeFilterValue}
									status="EXPIRED"
								/>
							);
							break;
					}

					return (
						<TabPanel key={i} value={tabValue} index={i}>
							<Suspense fallback={<div />}>{content}</Suspense>
						</TabPanel>
					);
				})}
			</Box>
		</div>
	);
};

export default OpenEnrollments;
